import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AsyncReactSelectMenu } from '../../../components/Dropdown/StyledReactSelect';
import {
  IUserGroupFieldsFragment,
  IUserGroupsQuery,
  IUserGroupsQueryVariables,
  UserGroupType,
} from '../../../utils/apollo/graphqlSchema';
import { GET_USER_GROUPS } from '../../../utils/apollo/permission';
import { useQuery } from '@apollo/client';
import { SingleValue } from 'react-select';
import { DictionaryTypeEnum, SortingKindEnum } from '../../../utils/interfaces';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setDictionaryListAction } from '../../../redux/slices/dictionary';
import * as FormUI from '../../pageForm/FormUI';
import FormHeaders from '../../pageForm/FormHeaders';
import GeneralFormItem from '../../../components/GeneralFormItem';
import { capitalize } from 'lodash';
import { appRoutes } from '../../../path';
import { Link } from 'react-router-dom';
import { theme } from '../../../styles/theme';
import { SquareRemoveButton } from '../../../components/Button';

interface IProps {
  userGroupIds: string[];
}

const groupHeaders = [
  {
    col: 'Name',
    text: 'Group',
    sortKind: SortingKindEnum.NAME,
    sortable: true,
    order: 1,
    align: 'left',
  },
  {
    col: 'GroupType',
    text: 'Group Type',
    sortable: true,
    sortKind: SortingKindEnum.TYPE,
    order: 2,
    align: 'left',
  },
  {
    col: 'Description',
    text: 'Description',
    sortable: false,
    order: 3,
    align: 'left',
  },
  {
    col: 'Inherited',
    text: 'Inherited User Role(s)',
    sortable: false,
    order: 4,
    align: 'left',
  },
  { col: 'Icon', text: '', sortable: false, order: 5, align: 'right' },
];
const groupFormColumnsWidth = '200px 150px minmax(0, 1fr) 150px 16px;';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const GroupSelectRow = styled.div`
  display: flex;
  column-gap: 8px;
  align-items: center;
  font-weight: 600;
  color: ${theme.colors.secondary800};
`;

/**
 * GroupAssignTable is a table to display selected groups and allow user to add/remove group from the list
 * @param userGroupIds - list of selected group ids
 */
const GroupAssignTable = ({ userGroupIds }: IProps) => {
  const dispatch = useAppDispatch();
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const selectedGroups = useAppSelector(
    (state) => state.dictionary.userGroups,
  ) as IUserGroupFieldsFragment[];

  const { refetch } = useQuery<IUserGroupsQuery, IUserGroupsQueryVariables>(
    GET_USER_GROUPS,
    {
      variables: {
        term: '',
        // fetch all and filter on UserGroupType.Kubit later to displaying all groups in dictionary and
        // support select UserGroupType.Kubit only in dropdown
        type: null,
        includeDisabled: false,
        includes: userGroupIds,
        pagination: { after: 0, first: 0 },
      },
    },
  );
  const onUserGroupSearchChange = async (term: string) => {
    const result = await refetch({
      term,
    });
    const fetchGroups = result?.data.userGroups?.entities;
    if (!fetchGroups) {
      return [];
    }
    const selectedGroups = fetchGroups.filter((group) =>
      userGroupIds.includes(group.id),
    );

    if (selectedGroups.length !== userGroupIds.length) {
      dispatch(
        setDictionaryListAction({
          newValues: selectedGroups,
          dictionaryType: DictionaryTypeEnum.UserGroups,
        }),
      );
    }

    // only allow select Kubit groups
    return fetchGroups.filter((group) => group.type === UserGroupType.Kubit);
  };

  const onSelectItem = (newValue: SingleValue<IUserGroupFieldsFragment>) => {
    if (
      !newValue ||
      selectedGroups.find((option) => option.id === newValue.id)
    ) {
      return;
    }
    dispatch(
      setDictionaryListAction({
        newValues: [...selectedGroups, newValue],
        dictionaryType: DictionaryTypeEnum.UserGroups,
      }),
    );
    setHighlightedId(newValue.id);

    // clear prev timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(() => {
      setHighlightedId(null);
    }, 3000);
    return;
  };
  const onRemoveItem = (id: string) => {
    const newGroups = selectedGroups.filter((group) => group.id !== id);
    dispatch(
      setDictionaryListAction({
        newValues: newGroups,
        dictionaryType: DictionaryTypeEnum.UserGroups,
      }),
    );
  };
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [timeoutIdRef]);
  return (
    <Container>
      <GroupSelectRow>
        <div>Assign Group(s)</div>
        <AsyncReactSelectMenu
          defaultOptions
          blurInputOnSelect
          promiseSearch={onUserGroupSearchChange}
          hideSelectedOptions
          value={selectedGroups}
          closeMenuOnSelect
          onChange={onSelectItem}
          getOptionLabel={(group) => group.name}
          getOptionValue={(group) => group.id}
          components={{
            SingleValue: () => null,
          }}
        />
      </GroupSelectRow>
      <FormUI.Container $noMarginBottom>
        <FormUI.Row $isHeader $gridTemplateColumns={groupFormColumnsWidth}>
          <FormHeaders
            dictionaryType={DictionaryTypeEnum.UserGroups}
            headerObject={groupHeaders}
          />
        </FormUI.Row>
        {selectedGroups?.map((group) => {
          const inheritedRoles = group.roles
            .map((role) => role.name)
            .join(', ');
          return (
            <FormUI.Row
              key={group.id}
              $isHighlighted={group.id === highlightedId}
              $gridTemplateColumns={groupFormColumnsWidth}
            >
              {/* Name and description */}
              <GeneralFormItem
                label={
                  <Link to={`${appRoutes.permission.userGroups}/${group.id}`}>
                    {group.name}
                  </Link>
                }
                tooltipContent={group.name}
              />
              <GeneralFormItem
                label={capitalize(group.type)}
                tooltipContent={capitalize(group.type)}
              />
              <GeneralFormItem
                label={group.description}
                tooltipContent={group.description}
              />
              <GeneralFormItem
                label={inheritedRoles}
                tooltipContent={inheritedRoles}
              />
              {group.type === UserGroupType.Kubit && (
                <SquareRemoveButton
                  onClick={() => onRemoveItem(group.id)}
                  content={'Remove Group'}
                />
              )}
            </FormUI.Row>
          );
        })}
      </FormUI.Container>
    </Container>
  );
};

export default GroupAssignTable;
